import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Alert } from '../../../components/Alert';
import { PageSpinner } from '../../../components/PageSpinner';
import { catalogueApi } from '../../../features/catalogue/catalogue.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { paths } from '../../../routes/paths';
import styles from '../AppPages.module.css';

export function PlansPage(): React.JSX.Element {
  const plans = useQuery({
    queryKey: ['catalogue', 'plans', 'active'],
    queryFn: () => catalogueApi.listCustomerRepaymentPlans({ status: 'ACTIVE', pageSize: 100 }),
  });

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Repayment plans</h1>
        <p className={styles.pageSubtitle}>
          Compare tenure and deposit options. Select a plan when starting an application.
        </p>
      </header>

      <section className={styles.card} aria-labelledby="plans-heading">
        <h2 className={styles.cardTitle} id="plans-heading">
          Active plans
        </h2>

        {plans.isPending && <PageSpinner label="Loading plans" />}
        {plans.isError && <Alert tone="error">{toDisplayMessage(plans.error)}</Alert>}

        {plans.isSuccess && plans.data.items.length === 0 && (
          <p className={styles.empty}>No repayment plans are available.</p>
        )}

        {plans.isSuccess && plans.data.items.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Plan</th>
                  <th scope="col">Deposit</th>
                  <th scope="col">Tenure</th>
                  <th scope="col">Price band</th>
                </tr>
              </thead>
              <tbody>
                {plans.data.items.map((plan) => (
                  <tr key={plan.id}>
                    <td>{plan.name}</td>
                    <td>
                      {plan.depositType === 'PERCENTAGE'
                        ? `${plan.depositValue}%`
                        : formatNgn(plan.depositValue)}
                    </td>
                    <td>
                      {plan.tenureMonths} months ({plan.repaymentFrequency.toLowerCase()})
                    </td>
                    <td>
                      {plan.minProductPrice !== null ? formatNgn(plan.minProductPrice) : '—'} –{' '}
                      {plan.maxProductPrice !== null ? formatNgn(plan.maxProductPrice) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p>
          <Link to={paths.customerApply}>Start an application</Link>
        </p>
      </section>
    </div>
  );
}
